import { useState, useEffect, useMemo } from 'react';
import { X, Save, FileText, Zap, Target, TrendingUp, AlertTriangle, Clock, Loader2, Calendar, Layers, Info } from 'lucide-react';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NumericInput } from '@/components/ui/numeric-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import type { CollectionStrategyItem, StrategyAction, TriggerConfig, AssignmentRule } from '@/types/collection-strategies';
import { StrategyStageEditor } from '@/components/settings/strategy-stage-editor';
import { StageFunnelTable } from '@/components/collections/stage-funnel-table';
import { useStrategyDiagnostics } from '@/hooks/useStrategyDiagnosticsGraphQL';
import { cn } from '@/lib/utils';
import { useStrategyById, useCreateStrategy, useUpdateStrategy, useStrategyTypeOptions } from '@/hooks/useStrategiesData';
import { useCollectionStrategyAuditLogsGraphQL } from '@/hooks/useCollectionStrategyAuditLogsGraphQL';

interface InlineStrategyMode {
  mode: 'create' | 'edit';
  strategy?: CollectionStrategyItem;
  onBack: () => void;
  onSaved: (strategy: CollectionStrategyItem, action: 'create' | 'edit') => void;
}

interface StrategyEditorPageProps {
  inlineMode?: InlineStrategyMode;
}

function getAuditActionColor(actionType: string) {
    switch (actionType) {
        case 'StatusChanged':
            return 'bg-orange-500';
        case 'StrategyCreated':
            return 'bg-blue-500';
        case 'FieldChanged':
            return 'bg-yellow-500';
        case 'RunNowTriggered':
            return 'bg-purple-500';
        case 'ScheduledRunStarted':
            return 'bg-cyan-500';
        case 'ScheduledRunCompleted':
            return 'bg-green-500';
        case 'ScheduledRunFailed':
            return 'bg-red-500';
        default:
            return 'bg-gray-400';
    }
}

function formatActionType(actionType: string) {
    return actionType.replace(/([A-Z])/g, ' $1').trim();
}

const DESCRIPTION_MAX_LENGTH = 1000;
const NAME_MAX_LENGTH = 200;

const DEFAULT_AUTOMATION_DAYS = {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
};

const DEFAULT_AI_CONTEXT_FACTORS = {
    paymentHistory: true,
    riskProfile: true,
    relationshipDuration: false,
    industryContext: false,
    economicFactors: false,
    previousDisputes: false,
    communicationPreferences: false,
};

function createDefaultFormData() {
    return {
        name: '',
        type: 'Standard' as CollectionStrategyItem['type'],
        description: '',
        status: 'Active' as 'Active' | 'Inactive',
        triggerType: 'days_overdue' as TriggerConfig['type'],
        daysOverdue: 0,
        amountThreshold: 5000,
        riskScoreThreshold: 70,
        actions: [] as StrategyAction[],
        assignmentRules: [] as AssignmentRule[],
        aiEnabled: false,
        aiTone: 3,
        aiFormality: 'professional' as 'casual' | 'professional' | 'formal',
        aiPersonalization: 'moderate' as 'minimal' | 'moderate' | 'deep',
        aiContextFactors: { ...DEFAULT_AI_CONTEXT_FACTORS },
        aiTimingOptimization: true,
        automationTime: '09:00',
        automationDays: { ...DEFAULT_AUTOMATION_DAYS },
        multiStageHandling: 'RunAllInOrder' as string,
        mergeUpToStage: 0,
    };
}

function buildFormDataFromStrategy(strategy: CollectionStrategyItem) {
    return {
        name: strategy.name,
        type: strategy.type,
        description: strategy.description,
        status: strategy.status,
        triggerType: strategy.trigger.type,
        daysOverdue: strategy.trigger.daysOverdue ?? 0,
        amountThreshold: strategy.trigger.amountThreshold || 5000,
        riskScoreThreshold: strategy.trigger.riskScoreThreshold || 70,
        actions: strategy.actions,
        assignmentRules: strategy.assignmentRules,
        aiEnabled: !!strategy.aiBehavior?.enabled,
        aiTone: strategy.aiBehavior?.communicationTone || 3,
        aiFormality: strategy.aiBehavior?.formalityLevel || 'professional',
        aiPersonalization: strategy.aiBehavior?.personalizationDepth || 'moderate',
        aiContextFactors: strategy.aiBehavior?.contextFactors || { ...DEFAULT_AI_CONTEXT_FACTORS },
        aiTimingOptimization: strategy.aiBehavior?.timingOptimization || true,
        automationTime: strategy.automationTime ?? '09:00',
        automationDays: strategy.automationDays ?? { ...DEFAULT_AUTOMATION_DAYS },
        multiStageHandling: strategy.multiStageHandling ?? 'RunAllInOrder',
        mergeUpToStage: strategy.mergeUpToStage ?? 0,
    };
}

function buildStrategyPayload(
    formData: ReturnType<typeof createDefaultFormData>,
    strategy: CollectionStrategyItem | null | undefined,
): CollectionStrategyItem {
    return {
        id: strategy?.id || '0',
        name: formData.name,
        type: formData.type,
        description: formData.description,
        status: formData.status,
        trigger: {
            type: formData.triggerType,
            daysOverdue: formData.triggerType === 'days_overdue' ? formData.daysOverdue : undefined,
            amountThreshold: formData.triggerType === 'amount_threshold' ? formData.amountThreshold : undefined,
            riskScoreThreshold: formData.triggerType === 'risk_score' ? formData.riskScoreThreshold : undefined,
        },
        actions: formData.actions,
        assignmentRules: formData.assignmentRules,
        aiBehavior: formData.aiEnabled ? {
            enabled: formData.aiEnabled,
            communicationTone: formData.aiTone as 1 | 2 | 3 | 4 | 5,
            formalityLevel: formData.aiFormality,
            personalizationDepth: formData.aiPersonalization,
            contextFactors: formData.aiContextFactors,
            timingOptimization: formData.aiTimingOptimization,
            escalationTriggers: strategy?.aiBehavior?.escalationTriggers || {
                noResponseDays: 7,
                brokenPromises: 2,
                disputeRaised: true,
            },
            humanReviewRequired: strategy?.aiBehavior?.humanReviewRequired ?? true,
        } : undefined,
        openCasesCount: strategy?.openCasesCount || 0,
        avgDaysOpen: strategy?.avgDaysOpen || 0,
        performanceMetrics: strategy?.performanceMetrics || {
            totalCasesProcessed: 0,
            successfulResolutions: 0,
            averageResolutionTime: 0,
            collectionRate: 0,
        },
        createdAt: strategy?.createdAt || new Date(),
        updatedAt: new Date(),
        automationTime: formData.automationTime,
        automationDays: formData.automationDays,
        multiStageHandling: formData.multiStageHandling,
        mergeUpToStage: formData.mergeUpToStage,
    };
}

function validateFormData(formData: { name: string; description: string; actions: { order: number; type: string; timing: { type: string }; templateName?: string }[]; automationDays: Record<string, boolean>; automationTime: string }): boolean {
    if (!formData.name.trim()) {
        toast.error('Please enter a strategy name');
        return false;
    }
    if (formData.name.length > NAME_MAX_LENGTH) {
        toast.error(`Strategy name must be ${NAME_MAX_LENGTH} characters or fewer`);
        return false;
    }
    if (!formData.description.trim()) {
        toast.error('Please enter a strategy description');
        return false;
    }
    if (formData.description.length > DESCRIPTION_MAX_LENGTH) {
        toast.error(`Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer`);
        return false;
    }
    if (formData.actions.length === 0) {
        toast.error('A strategy must have at least one stage');
        return false;
    }
    const sortedActions = [...formData.actions].sort((a, b) => a.order - b.order);
    if (sortedActions.length > 0 && sortedActions[0].timing.type !== 'days_from_trigger') {
        toast.error('Stage 1 must have a Timing Type of "Days from Trigger"');
        return false;
    }
    const emailActionsWithoutTemplate = formData.actions.filter(
        (a) => a.type === 'email' && !a.templateName?.trim()
    );
    if (emailActionsWithoutTemplate.length > 0) {
        toast.error('All email stages must have a template selected');
        return false;
    }
    const hasAtLeastOneDay = Object.values(formData.automationDays).some(Boolean);
    if (!hasAtLeastOneDay) {
        toast.error('Please select at least one day for automation frequency');
        return false;
    }
    if (!formData.automationTime) {
        toast.error('Please set a time for automation frequency');
        return false;
    }
    return true;
}

function resolveStrategyId(
    inlineMode: InlineStrategyMode | undefined,
    strategyIdParam: string | undefined,
    searchParams: URLSearchParams,
    strategyFromState: CollectionStrategyItem | null,
): string | null {
    if (inlineMode) {
        return inlineMode.strategy?.id ?? null;
    }
    return strategyIdParam || searchParams.get('id') || strategyFromState?.id || null;
}

function resolveMode(
    inlineMode: InlineStrategyMode | undefined,
    searchParams: URLSearchParams,
    locationState: { mode?: string } | null,
    strategyId: string | null,
): 'create' | 'edit' {
    if (inlineMode) {
        return inlineMode.mode;
    }
    return (searchParams.get('mode') || locationState?.mode || (strategyId ? 'edit' : 'create')) as 'create' | 'edit';
}

function resolveStrategyIdForUpdate(strategyId: string | null): string | number {
    if (!strategyId) return 0;
    const asNumber = Number(strategyId);
    return isNaN(asNumber) ? strategyId : parseInt(strategyId, 10);
}

function hasUnsavedChanges(
    formData: ReturnType<typeof createDefaultFormData>,
    strategy: CollectionStrategyItem | null | undefined,
    mode: 'create' | 'edit',
): boolean {
    if (mode === 'create') {
        return formData.name !== '' || formData.description !== '';
    }
    if (strategy) {
        return (
            formData.name !== strategy.name ||
            formData.type !== strategy.type ||
            formData.description !== strategy.description ||
            formData.status !== strategy.status ||
            formData.triggerType !== strategy.trigger.type ||
            formData.daysOverdue !== (strategy.trigger.daysOverdue ?? 0) ||
            formData.amountThreshold !== (strategy.trigger.amountThreshold || 5000) ||
            formData.riskScoreThreshold !== (strategy.trigger.riskScoreThreshold || 70) ||
            formData.automationTime !== (strategy.automationTime ?? '09:00') ||
            JSON.stringify(formData.automationDays) !== JSON.stringify(strategy.automationDays ?? {
                monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false,
            }) ||
            formData.multiStageHandling !== (strategy.multiStageHandling ?? 'RunAllInOrder') ||
            formData.mergeUpToStage !== (strategy.mergeUpToStage ?? 0)
        );
    }
    return false;
}

// ---------------------------------------------------------------------------
// Sub-components (defined outside StrategyEditorPage to keep complexity low)
// ---------------------------------------------------------------------------

interface SettingsTabContentProps {
    formData: ReturnType<typeof createDefaultFormData>;
    setFormData: React.Dispatch<React.SetStateAction<ReturnType<typeof createDefaultFormData>>>;
    strategyTypeOptions: { id: string | number; name: string }[];
    handleActionsChange: (actions: StrategyAction[]) => void;
    isLoadingStrategy: boolean;
    mode: 'create' | 'edit';
}

function SettingsTabContent({ formData, setFormData, strategyTypeOptions, handleActionsChange, isLoadingStrategy, mode }: SettingsTabContentProps) {
    if (isLoadingStrategy && mode === 'edit') {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">Loading strategy...</span>
            </div>
        );
    }

    const handleDayChange = (dayKey: string, checked: boolean | 'indeterminate') => {
        setFormData((prev) => ({
            ...prev,
            automationDays: {
                ...prev.automationDays,
                [dayKey]: checked === true,
            },
        }));
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Row 1: Overview and Triggers Side-by-Side */}
            <div className="grid grid-cols-2 gap-6">
                {/* Panel 1: Overview */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-blue-500" />
                            Overview
                        </CardTitle>
                        <CardDescription>
                            Basic information about the collection strategy
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label>Strategy name *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., Standard 30-Day Collection"
                                maxLength={NAME_MAX_LENGTH}
                            />
                        </div>
                        <div>
                            <Label>Type *</Label>
                            <Select
                                value={formData.type}
                                onValueChange={(value: CollectionStrategyItem['type']) => setFormData((prev) => ({ ...prev, type: value }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {strategyTypeOptions.length > 0 ? (
                                        strategyTypeOptions.map((option) => (
                                            <SelectItem key={option.id} value={option.name}>
                                                {option.name}
                                            </SelectItem>
                                        ))
                                    ) : (
                                        <>
                                            <SelectItem value="Standard">Standard</SelectItem>
                                            <SelectItem value="Intensive">Intensive</SelectItem>
                                            <SelectItem value="VIP">VIP</SelectItem>
                                            <SelectItem value="High Risk">High Risk</SelectItem>
                                        </>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Description *</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                                placeholder="Describe when and how this strategy should be used..."
                                rows={4}
                                maxLength={DESCRIPTION_MAX_LENGTH}
                            />
                            <p className="text-xs text-muted-foreground mt-1 text-right">
                                {formData.description.length}/{DESCRIPTION_MAX_LENGTH}
                            </p>
                        </div>
                        <div>
                            <div className="flex items-center gap-1.5 mb-1">
                                <Label>Multi-Stage Handling</Label>
                                <TooltipProvider delayDuration={300}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Info className="h-4 w-4 text-muted-foreground cursor-help flex-shrink-0" />
                                        </TooltipTrigger>
                                        <TooltipContent side="right" className="max-w-xs text-xs">
                                            <p className="font-medium mb-1">Controls how stages are processed when multiple stages become eligible in a single strategy run.</p>
                                            <p className="mb-0.5"><strong>Run All in Order</strong> — Every triggered stage is processed one after another in sequence.</p>
                                            <p className="mb-0.5"><strong>Skip to Latest</strong> — Only the most recent stage is processed; earlier stages are skipped.</p>
                                            <p><strong>One Per Run</strong> — Only one stage is processed per run. Remaining stages are picked up in subsequent runs.</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <Select
                                value={formData.multiStageHandling}
                                onValueChange={(value: string) => setFormData((prev) => ({ ...prev, multiStageHandling: value }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="RunAllInOrder">Run All in Order</SelectItem>
                                    <SelectItem value="SkipToLatest">Skip to Latest</SelectItem>
                                    <SelectItem value="OnePerRun">One Per Run</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <div className="flex items-center gap-1.5 mb-1">
                                <Label>Merge Up To Stage</Label>
                                <TooltipProvider delayDuration={300}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Info className="h-4 w-4 text-muted-foreground cursor-help flex-shrink-0" />
                                        </TooltipTrigger>
                                        <TooltipContent side="right" className="max-w-xs text-xs">
                                            <p className="font-medium mb-1">Controls how far into a strategy new invoices can be merged into an existing case.</p>
                                            <p className="mb-0.5"><strong>Stage 0</strong> (default) — New invoices only merge into cases that haven't started processing yet.</p>
                                            <p>Higher values allow merging into cases that have already progressed through earlier stages.</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <Select
                                value={String(formData.mergeUpToStage)}
                                onValueChange={(value: string) => setFormData((prev) => ({ ...prev, mergeUpToStage: Number(value) }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="0">Stage 0 — Before first action (default)</SelectItem>
                                    {formData.actions.map((action, index) => (
                                        <SelectItem key={index + 1} value={String(index + 1)}>
                                            Up to Stage {index + 1} — {action.type === 'email' ? 'Email' : action.type === 'phone_call' ? 'Phone Call' : action.type === 'escalation' ? 'Escalation' : action.type === 'account_action' ? 'Place On Stop' : action.type.replace('_', ' ')}
                                            {action.templateName ? ` - ${action.templateName}` : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Panel 2: Triggers */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Zap className="h-5 w-5 text-amber-500" />
                            Triggers
                        </CardTitle>
                        <CardDescription>
                            Define when this strategy should be automatically applied
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label>Trigger Type</Label>
                            <Select
                                value={formData.triggerType}
                                onValueChange={(value: 'days_overdue' | 'amount_threshold' | 'risk_score' | 'manual_only' | 'payment_pattern_change' | 'promise_broken') => setFormData((prev) => ({ ...prev, triggerType: value }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="days_overdue">Days Overdue</SelectItem>
                                    <SelectItem value="amount_threshold">Amount Threshold</SelectItem>
                                    {/* <SelectItem value="risk_score">Risk Score</SelectItem> */}
                                    {/* <SelectItem value="manual_only">Manual Only</SelectItem> */}
                                    {/* <SelectItem value="payment_pattern_change">Payment Pattern Change</SelectItem> */}
                                    {/* <SelectItem value="promise_broken">Promise Broken</SelectItem> */}
                                </SelectContent>
                            </Select>
                        </div>

                        {formData.triggerType === 'days_overdue' && (
                            <div>
                                <Label>Days Overdue Threshold</Label>
                                <NumericInput
                                    value={formData.daysOverdue}
                                    onChange={(value) => setFormData((prev) => ({ ...prev, daysOverdue: value ?? 0 }))}
                                    integer
                                />
                                <p className="text-sm text-muted-foreground mt-1">
                                    Trigger when invoice is overdue by this many days
                                </p>
                            </div>
                        )}

                        {formData.triggerType === 'amount_threshold' && (
                            <div>
                                <Label>Amount Threshold (£)</Label>
                                <NumericInput
                                    value={formData.amountThreshold}
                                    onChange={(value) => setFormData((prev) => ({ ...prev, amountThreshold: value ?? 0 }))}
                                    integer={false}
                                />
                                <p className="text-sm text-muted-foreground mt-1">
                                    Trigger when outstanding amount exceeds this value
                                </p>
                            </div>
                        )}

                        {formData.triggerType === 'risk_score' && (
                            <div>
                                <Label>Risk Score Threshold</Label>
                                <NumericInput
                                    value={formData.riskScoreThreshold}
                                    onChange={(value) => setFormData((prev) => ({ ...prev, riskScoreThreshold: value ?? 0 }))}
                                    integer
                                    sign="positive"
                                    min={0}
                                    max={100}
                                />
                                <p className="text-sm text-muted-foreground mt-1">
                                    Trigger when customer risk score reaches or exceeds this value (0-100)
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Panel 3: Stages */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-green-500" />
                        Stages
                    </CardTitle>
                    <CardDescription>
                        Configure the collection actions to be performed
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <StrategyStageEditor
                        actions={formData.actions}
                        onActionsChange={handleActionsChange}
                    />
                </CardContent>
            </Card>

            {/* Panel 4: Automation Frequency */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-purple-500" />
                        Automation Frequency
                    </CardTitle>
                    <CardDescription>
                        Configure when this strategy should run automatically
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Field 1: Time */}
                    <div>
                        <Label>Time *</Label>
                        <Select
                            value={formData.automationTime}
                            onValueChange={(value: string) => setFormData((prev) => ({ ...prev, automationTime: value }))}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="00:00">12:00 AM</SelectItem>
                                <SelectItem value="01:00">1:00 AM</SelectItem>
                                <SelectItem value="02:00">2:00 AM</SelectItem>
                                <SelectItem value="03:00">3:00 AM</SelectItem>
                                <SelectItem value="04:00">4:00 AM</SelectItem>
                                <SelectItem value="05:00">5:00 AM</SelectItem>
                                <SelectItem value="06:00">6:00 AM</SelectItem>
                                <SelectItem value="07:00">7:00 AM</SelectItem>
                                <SelectItem value="08:00">8:00 AM</SelectItem>
                                <SelectItem value="09:00">9:00 AM</SelectItem>
                                <SelectItem value="10:00">10:00 AM</SelectItem>
                                <SelectItem value="11:00">11:00 AM</SelectItem>
                                <SelectItem value="12:00">12:00 PM</SelectItem>
                                <SelectItem value="13:00">1:00 PM</SelectItem>
                                <SelectItem value="14:00">2:00 PM</SelectItem>
                                <SelectItem value="15:00">3:00 PM</SelectItem>
                                <SelectItem value="16:00">4:00 PM</SelectItem>
                                <SelectItem value="17:00">5:00 PM</SelectItem>
                                <SelectItem value="18:00">6:00 PM</SelectItem>
                                <SelectItem value="19:00">7:00 PM</SelectItem>
                                <SelectItem value="20:00">8:00 PM</SelectItem>
                                <SelectItem value="21:00">9:00 PM</SelectItem>
                                <SelectItem value="22:00">10:00 PM</SelectItem>
                                <SelectItem value="23:00">11:00 PM</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-sm text-muted-foreground mt-1">
                            Select the time for automation execution
                        </p>
                    </div>

                    {/* Field 2: Days of the Week */}
                    <div>
                        <Label>Days of the Week *</Label>
                        <div className="mt-2 space-y-2">
                            {[
                                { key: 'monday', label: 'Monday' },
                                { key: 'tuesday', label: 'Tuesday' },
                                { key: 'wednesday', label: 'Wednesday' },
                                { key: 'thursday', label: 'Thursday' },
                                { key: 'friday', label: 'Friday' },
                                { key: 'saturday', label: 'Saturday' },
                                { key: 'sunday', label: 'Sunday' },
                            ].map((day) => (
                                <div key={day.key} className="flex items-center space-x-2">
                                    <Checkbox
                                        id={day.key}
                                        checked={formData.automationDays[day.key as keyof typeof formData.automationDays]}
                                        onCheckedChange={(checked) => handleDayChange(day.key, checked)}
                                    />
                                    <label
                                        htmlFor={day.key}
                                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                    >
                                        {day.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                            Select which days the strategy should run (at least one required)
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

interface PerformanceTabContentProps {
    diagnostics: ReturnType<typeof useStrategyDiagnostics>['data'];
    isLoadingDiagnostics: boolean;
    hasIssues: boolean | null;
    isActive: boolean;
}

function PerformanceTabContent({ diagnostics, isLoadingDiagnostics, hasIssues, isActive }: PerformanceTabContentProps) {
    if (isLoadingDiagnostics) {
        return (
            <div className="max-w-7xl mx-auto flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">Loading performance data...</span>
            </div>
        );
    }

    if (!diagnostics) {
        return (
            <div className="max-w-7xl mx-auto flex flex-col items-center justify-center py-16">
                <TrendingUp className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-muted-foreground mb-2">No Performance Data Available</h3>
                <p className="text-sm text-muted-foreground text-center max-w-md">
                    Performance diagnostics will appear here once the strategy has been run and data has been collected.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Warning Banner */}
            {hasIssues && (
                <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <h4 className="text-base font-semibold text-amber-900 mb-1">
                                Strategy Issue Detected
                            </h4>
                            <p className="text-sm text-amber-800 mb-2">
                                {diagnostics.impactAnalysis.notReceivingEmails
                                    ? `${diagnostics.impactAnalysis.notReceivingEmails.count} customers did not receive follow-up emails`
                                    : 'This strategy requires attention'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Status Overview Card — full width */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-teal-600" />
                        Status Overview
                    </CardTitle>
                    <CardDescription>
                        Current status and key metrics
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-5 gap-x-4">
                        {/* Created */}
                        <div className="space-y-0.5">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Created</p>
                            <p className="text-sm font-semibold text-gray-900">
                                {format(diagnostics.createdAt, 'dd MMM yyyy')}
                            </p>
                            <p className="text-xs text-gray-600">
                                by {diagnostics.createdBy.name}
                            </p>
                        </div>

                        {/* Open Cases */}
                        <div className="space-y-0.5">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Open Cases</p>
                            <div className="flex items-center gap-2">
                                <Target className="h-5 w-5 text-teal-600" />
                                <p className="text-2xl font-bold text-gray-900">
                                    {diagnostics.activeAccounts}
                                </p>
                            </div>
                        </div>

                        {/* Last Run */}
                        <div className="space-y-0.5">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Last Run</p>
                            {diagnostics.lastRun ? (
                                <>
                                    <p className="text-sm font-semibold text-gray-900">
                                        {format(diagnostics.lastRun, 'dd MMM yyyy, h:mmaaa')}
                                    </p>
                                    <p className="text-xs text-gray-600">
                                        {formatDistanceToNow(diagnostics.lastRun, { addSuffix: true })}
                                    </p>
                                </>
                            ) : (
                                <p className="text-sm text-gray-400">—</p>
                            )}
                        </div>

                        {/* Emails Sent */}
                        <div className="space-y-0.5">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Emails Sent (14 Days)</p>
                            <div className="flex items-center gap-2">
                                {diagnostics.emailsSent14Days === 0 && (
                                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                                )}
                                <p className={cn(
                                    'text-2xl font-bold',
                                    diagnostics.emailsSent14Days === 0 ? 'text-red-600' : 'text-gray-900'
                                )}>
                                    {diagnostics.emailsSent14Days}
                                </p>
                            </div>
                            {diagnostics.expectedEmails && (
                                <p className="text-xs text-gray-600">
                                    Expected: ~{diagnostics.expectedEmails}
                                </p>
                            )}
                        </div>

                        {/* Next Scheduled Run */}
                        <div className="space-y-0.5">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Next Scheduled Run</p>
                            {diagnostics.nextScheduledRun && isActive ? (
                                <>
                                    <p className="text-sm font-semibold text-gray-900">
                                        {format(diagnostics.nextScheduledRun, 'dd MMM yyyy, h:mmaaa')}
                                    </p>
                                    <p className="text-xs text-green-600">
                                        {formatDistanceToNow(diagnostics.nextScheduledRun, { addSuffix: true })}
                                    </p>
                                </>
                            ) : (
                                <p className="text-sm text-gray-400">—</p>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Stage Breakdown Card — full width below Status Overview */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Layers className="h-5 w-5 text-teal-600" />
                        Stage Breakdown
                    </CardTitle>
                    <CardDescription>
                        Number of cases at each stage of this strategy
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <StageFunnelTable data={diagnostics.stageFunnel} />
                </CardContent>
            </Card>
        </div>
    );
}

interface AuditLogTabContentProps {
    auditLogs: ReturnType<typeof useCollectionStrategyAuditLogsGraphQL>['auditLogs'];
    isLoadingAuditLogs: boolean;
    isFetchingMoreAuditLogs: boolean;
    hasMoreAuditLogs: boolean;
    loadMoreAuditLogs: () => void;
}

function AuditLogTabContent({ auditLogs, isLoadingAuditLogs, isFetchingMoreAuditLogs, hasMoreAuditLogs, loadMoreAuditLogs }: AuditLogTabContentProps) {
    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Audit Trail Card */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-blue-600" />
                        Audit Trail
                    </CardTitle>
                    <CardDescription>
                        Recent changes and actions
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {isLoadingAuditLogs ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            <span className="ml-2 text-muted-foreground">Loading audit logs...</span>
                        </div>
                    ) : (
                        <>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date & Time</TableHead>
                                        <TableHead>Action</TableHead>
                                        <TableHead>Description</TableHead>
                                        <TableHead>Field</TableHead>
                                        <TableHead>Old Value</TableHead>
                                        <TableHead>New Value</TableHead>
                                        <TableHead>User</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {auditLogs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                                                No audit log entries found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        auditLogs.map((log) => (
                                            <TableRow key={log.id}>
                                                <TableCell className="whitespace-nowrap">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {format(log.createdAt, 'dd MMM yyyy')}
                                                        </span>
                                                        <span className="text-xs text-gray-500">
                                                            {format(log.createdAt, 'h:mm aaa')}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className={cn(
                                                                'w-2 h-2 rounded-full flex-shrink-0',
                                                                getAuditActionColor(log.actionType)
                                                            )}
                                                        />
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {formatActionType(log.actionType)}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-sm text-gray-600 break-words">
                                                        {log.description}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    {log.fieldName ? (
                                                        <span className="text-sm text-gray-900">{log.fieldName}</span>
                                                    ) : (
                                                        <span className="text-sm text-gray-400">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {log.oldValue ? (
                                                        <span className="text-sm text-gray-600 break-words">{log.oldValue}</span>
                                                    ) : (
                                                        <span className="text-sm text-gray-400">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {log.newValue ? (
                                                        <span className="text-sm text-gray-600 break-words">{log.newValue}</span>
                                                    ) : (
                                                        <span className="text-sm text-gray-400">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="whitespace-nowrap">
                                                    <span className="text-sm text-gray-900">
                                                        {log.createdBy}
                                                    </span>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                            {hasMoreAuditLogs && (
                                <div className="flex justify-center pt-4">
                                    <Button
                                        variant="outline"
                                        onClick={loadMoreAuditLogs}
                                        disabled={isFetchingMoreAuditLogs}
                                    >
                                        {isFetchingMoreAuditLogs ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : null}
                                        Load More
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export function StrategyEditorPage({ inlineMode }: StrategyEditorPageProps = {}) {
    const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { strategyId: strategyIdParam } = useParams<{ strategyId: string }>();

    // Get strategy and mode from location state, URL params, or inlineMode
    const strategyFromState = inlineMode?.strategy ?? (location.state?.strategy as CollectionStrategyItem | null);
    const strategyId = resolveStrategyId(inlineMode, strategyIdParam, searchParams, strategyFromState);
    const mode = resolveMode(inlineMode, searchParams, location.state as { mode?: string } | null, strategyId);

    // Fetch strategy data from API when editing (for live data mode)
    // Pass strategyId directly (can be string or number)
    const { data: strategyFromApi, isLoading: isLoadingStrategy } = useStrategyById(strategyId);

    // Use API data if available, otherwise fall back to state data.
    // Prefer openCasesCount from list/state data (GraphQL-based, dynamically computed)
    // over REST detail data (stored column, may be stale).
    const strategy = useMemo(() => {
        if (strategyFromApi) {
            return { ...strategyFromApi, openCasesCount: strategyFromState?.openCasesCount ?? strategyFromApi.openCasesCount };
        }
        return strategyFromState;
    }, [strategyFromApi, strategyFromState]);

    // Mutations for create and update
    const createStrategyMutation = useCreateStrategy({ forceLive: !!inlineMode });
    // For update, try to parse as number, otherwise use the string ID
    const strategyIdForUpdate = resolveStrategyIdForUpdate(strategyId);
    const updateStrategyMutation = useUpdateStrategy(strategyIdForUpdate, { forceLive: !!inlineMode });

    // Lookup data for dropdowns (cached for 1 minute)
    const { data: strategyTypeOptions = [] } = useStrategyTypeOptions();

    const [formData, setFormData] = useState(createDefaultFormData);

    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
    const [showActiveCasesWarningDialog, setShowActiveCasesWarningDialog] = useState(false);

    // Get initial tab from URL parameter, default to 'performance' if coming from dashboard
    const initialTab = searchParams.get('tab') || 'settings';
    const [activeTab, setActiveTab] = useState(initialTab);
    const { data: diagnostics = null, isLoading: isLoadingDiagnostics } = useStrategyDiagnostics(
        mode === 'edit' ? strategyId : null
    );

    // Initialize form data when strategy changes
    useEffect(() => {
        if (strategy && mode === 'edit') {
            setFormData(buildFormDataFromStrategy(strategy));
        } else {
            setFormData(createDefaultFormData());
        }
    }, [strategy, mode]);

    const performSave = async () => {
        if (!validateFormData(formData)) return;

        const strategyData = buildStrategyPayload(formData, strategy);

        try {
            if (mode === 'create') {
                await createStrategyMutation.mutateAsync(strategyData);
                toast.success('Strategy created successfully');
            } else {
                await updateStrategyMutation.mutateAsync(strategyData);
                toast.success('Strategy updated successfully');
            }
            if (inlineMode) {
                inlineMode.onSaved(strategyData as CollectionStrategyItem, mode);
            } else {
                navigate('/settings/strategies');
            }
        } catch (error: unknown) {
            const fallbackMessage = mode === 'create' ? 'Failed to create strategy' : 'Failed to update strategy';
            if (error instanceof Error && error.message) {
                toast.error(error.message);
            } else {
                toast.error(fallbackMessage);
            }
            console.error('Save strategy error:', error);
        }
    };

    const handleSave = async () => {
        // Basic validation
        if (!formData.name.trim()) {
            toast.error('Please enter a strategy name');
            return;
        }
        if (!formData.description.trim()) {
            toast.error('Please enter a strategy description');
            return;
        }

        // In edit mode, warn if there are active cases
        if (mode === 'edit' && (strategy?.openCasesCount ?? 0) > 0) {
            setShowActiveCasesWarningDialog(true);
            return;
        }

        await performSave();
    };

    const handleCancel = () => {
        if (hasUnsavedChanges(formData, strategy, mode)) {
            setPendingNavigation('/settings/strategies');
            setShowUnsavedChangesDialog(true);
        } else {
            if (inlineMode) {
                inlineMode.onBack();
            } else {
                navigate('/settings/strategies');
            }
        }
    };

    const confirmNavigation = () => {
        if (inlineMode) {
            inlineMode.onBack();
        } else if (pendingNavigation) {
            navigate(pendingNavigation);
        }
        setShowUnsavedChangesDialog(false);
        setPendingNavigation(null);
    };

    const cancelNavigation = () => {
        setShowUnsavedChangesDialog(false);
        setPendingNavigation(null);
    };

    const handleActionsChange = (actions: StrategyAction[]) => {
        setFormData((prev) => ({
            ...prev,
            actions,
            mergeUpToStage: Math.min(prev.mergeUpToStage, actions.length),
        }));
    };

    const handleConfirmActiveCasesSave = async () => {
        setShowActiveCasesWarningDialog(false);
        await performSave();
    };

    // Audit log data from GraphQL
    const {
        auditLogs,
        isLoading: isLoadingAuditLogs,
        isFetchingMore: isFetchingMoreAuditLogs,
        hasMore: hasMoreAuditLogs,
        loadMore: loadMoreAuditLogs,
    } = useCollectionStrategyAuditLogsGraphQL(strategyId);

    const hasIssues = diagnostics && (diagnostics.emailsSent14Days === 0 || diagnostics.status === 'Inactive');
    const isActive = diagnostics?.status === 'Active';

    if (isAdminLoading) {
        return (
            <div className="flex min-h-[50vh] w-full items-center justify-center">
                <output
                    aria-label="Loading"
                    className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
                />
            </div>
        );
    }
    if (!isAdmin) return <AdminOnlyEmptyState />;

    return (
        <div className="flex-1 flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="shrink-0 border-b bg-background px-8 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <h1 className="text-3xl font-bold tracking-tight">
                            {mode === 'create' ? 'Create Strategy' : `Edit Strategy: ${strategy?.name}`}
                        </h1>
                        <p className="text-muted-foreground mt-2">
                            {mode === 'create'
                                ? 'Create a new collection strategy with automated workflows'
                                : 'Modify the strategy settings and configuration'
                            }
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={handleCancel}>
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={!formData.name || !formData.description || createStrategyMutation.isPending || updateStrategyMutation.isPending || (isLoadingStrategy && mode === 'edit')}
                        >
                            {(createStrategyMutation.isPending || updateStrategyMutation.isPending) ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="h-4 w-4 mr-2" />
                            )}
                            {mode === 'create' ? 'Create Strategy' : 'Save Changes'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Content - Tabbed Layout */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                    <div className="shrink-0 border-b bg-background px-8">
                        <TabsList className="h-12">
                            <TabsTrigger value="settings" className="gap-2">
                                <FileText className="h-4 w-4" />
                                Settings
                            </TabsTrigger>
                            {mode === 'edit' && (
                                <TabsTrigger value="performance" className="gap-2">
                                    <TrendingUp className="h-4 w-4" />
                                    Performance
                                </TabsTrigger>
                            )}
                            {mode === 'edit' && (
                                <TabsTrigger value="audit-log" className="gap-2">
                                    <Clock className="h-4 w-4" />
                                    Audit Log
                                </TabsTrigger>
                            )}
                        </TabsList>
                    </div>

                    {/* Settings Tab */}
                    <TabsContent value="settings" className="flex-1 overflow-y-auto px-8 py-6 mt-0">
                        <SettingsTabContent
                            formData={formData}
                            setFormData={setFormData}
                            strategyTypeOptions={strategyTypeOptions}
                            handleActionsChange={handleActionsChange}
                            isLoadingStrategy={isLoadingStrategy}
                            mode={mode}
                        />
                    </TabsContent>

                    {/* Performance Tab */}
                    {mode === 'edit' && (
                        <TabsContent value="performance" className="flex-1 overflow-y-auto px-8 py-6 mt-0">
                            <PerformanceTabContent
                                diagnostics={diagnostics}
                                isLoadingDiagnostics={isLoadingDiagnostics}
                                hasIssues={hasIssues}
                                isActive={isActive}
                            />
                        </TabsContent>
                    )}

                    {/* Audit Log Tab */}
                    {mode === 'edit' && (
                        <TabsContent value="audit-log" className="flex-1 overflow-y-auto px-8 py-6 mt-0">
                            <AuditLogTabContent
                                auditLogs={auditLogs}
                                isLoadingAuditLogs={isLoadingAuditLogs}
                                isFetchingMoreAuditLogs={isFetchingMoreAuditLogs}
                                hasMoreAuditLogs={hasMoreAuditLogs}
                                loadMoreAuditLogs={loadMoreAuditLogs}
                            />
                        </TabsContent>
                    )}
                </Tabs>

            {/* Unsaved Changes Dialog */}
            <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={cancelNavigation}>Stay on Page</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmNavigation}>Leave Without Saving</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Active Cases Warning Dialog */}
            <AlertDialog open={showActiveCasesWarningDialog} onOpenChange={setShowActiveCasesWarningDialog}>
                <AlertDialogContent className="max-w-lg">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            Save Changes to Active Strategy?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm text-foreground">
                                <p>
                                    This strategy has{' '}
                                    <span className="font-semibold">
                                        {strategy?.openCasesCount} active case{strategy?.openCasesCount === 1 ? '' : 's'}
                                    </span>{' '}
                                    that are currently being processed.
                                </p>
                                <p>
                                    Saving these stage changes will affect what happens next for all cases that haven't yet completed the amended stages — they will follow the new stage configuration from their current position onwards. Completed stages are not affected.
                                </p>
                                <p className="font-medium">Are you sure you want to continue?</p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setShowActiveCasesWarningDialog(false)}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmActiveCasesSave}>
                            Yes, Save Changes
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default StrategyEditorPage;
